import type { DraftScratchpad } from './types.js';

// Deep clone for the dirty baseline. Plain JSON would lose the
// `Date`/`Map` exotic types if any ever join the scratchpad; field-by-
// field gives us a single edit point that mirrors the store actions.
export function cloneDraft(d: DraftScratchpad): DraftScratchpad {
  return {
    manager: d.manager,
    name: d.name,
    kind: d.kind,
    transportTypeId: d.transportTypeId,
    route: { from: d.route.from, to: d.route.to },
    destinations: d.destinations.map((dest) => ({
      arrivalCityId: dest.arrivalCityId,
      sortOrder: dest.sortOrder,
    })),
    deliveryAddress: d.deliveryAddress,
    // WHY (#1393): mirror `deliveryAddress` — clone the pickup address
    //   by value into the baseline so dirty-detection compares exactly.
    pickupAddress: d.pickupAddress,
    // WHY (#1061): clone the insurance triplet into the baseline so
    //   dirty-detection compares apples to apples. Omitted when the
    //   source omits the key so legacy fixtures round-trip to
    //   deep-equal under `expect(...).toEqual(...)`.
    ...(d.insuranceRequested !== undefined ? { insuranceRequested: d.insuranceRequested } : {}),
    ...(d.insuranceCargoValue !== undefined ? { insuranceCargoValue: d.insuranceCargoValue } : {}),
    ...(d.insuranceCargoCurrency !== undefined
      ? { insuranceCargoCurrency: d.insuranceCargoCurrency }
      : {}),
    // WHY (#996): clone the Roads/Packaging arrays by value so the
    //   baseline does not alias the live store slot. Omitted entirely
    //   when the source omits the key so legacy fixtures round-trip
    //   to deep-equal under `expect(...).toEqual(...)`.
    ...(d.roadOptionIds !== undefined ? { roadOptionIds: [...d.roadOptionIds] } : {}),
    ...(d.packagingOptionIds !== undefined
      ? { packagingOptionIds: [...d.packagingOptionIds] }
      : {}),
    // WHY (#1448): clone the STANDARD request-level totals into the
    //   baseline so dirty-detection compares them by value.
    totalWeight: d.totalWeight,
    totalVolume: d.totalVolume,
    items: d.items.map((it) => ({
      ...it,
      photos: [...it.photos],
      packages: it.packages.map((p) => ({ ...p })),
    })),
    boxes: d.boxes.map((b) => ({
      ...b,
      items: b.items.map((it) => ({ ...it, photos: [...it.photos] })),
    })),
    boxesSectionOpen: d.boxesSectionOpen,
    clientToken: d.clientToken,
    status: d.status,
    returnReason: d.returnReason,
  };
}

export function collectPhotoKeys(d: DraftScratchpad): string[] {
  const out: string[] = [];
  for (const it of d.items) {
    for (const k of it.photos) out.push(k);
  }
  // WHY (#599): nested-item photos are saved references too. Without
  //   them in the baseline set, a successful save would leave nested
  //   keys in `unsavedPhotoKeys` and the discard path would attempt
  //   to delete them — the server-side reference check protects the
  //   bytes, but the cleanup batch would surface noisy `failed`
  //   entries on every close. Including them mirrors the behaviour
  //   of ordinary item photos.
  for (const b of d.boxes) {
    for (const it of b.items) {
      for (const k of it.photos) out.push(k);
    }
  }
  return out;
}

/**
 * @aiPurpose Pure dirty-check: compare the live scratchpad to the
 *   baseline that mirrors the last server-saved state. Returns `true`
 *   when no baseline exists (a never-saved scratchpad with at least
 *   one user edit reaches dirty=true through the same path because
 *   even an empty scratchpad differs from `null` baseline). Lives
 *   outside the store so React selectors that subscribe to derived
 *   state stay stable across unrelated UI ticks.
 */
export function isDraftDirty(
  draft: DraftScratchpad | null,
  baseline: DraftScratchpad | null,
): boolean {
  if (draft === null) return false;
  if (baseline === null) {
    // INVARIANT: strict character-equality against the seeded defaults
    // (`name=''`, default route, empty items). Any deviation — including
    // pure-whitespace input that the user typed — is dirty so the
    // dirty-close guard cannot silently drop characters the user expects
    // their save/discard choice to cover.
    if (draft.name.length > 0) return true;
    if (draft.items.length > 0) return true;
    if (draft.boxes.length > 0) return true;
    if (draft.route.from !== 'Китай' || draft.route.to !== 'Россия') return true;
    // WHY (#1057): a fresh CONTAINER draft seeds `deliveryAddress: null`;
    //   typing anything must flip the editor into dirty state so the
    //   close guard fires and the value isn't silently discarded. Mirror
    //   the canonicalize path used for persisted drafts.
    if (draft.deliveryAddress !== null) return true;
    // WHY (#1393): mirror `deliveryAddress` — a fresh CONTAINER draft
    //   seeds `pickupAddress: null`; typing anything must flip dirty so
    //   the close guard fires and the value isn't silently discarded.
    if (draft.pickupAddress !== null) return true;
    // WHY (#1061): toggling insurance on, or filling in any of the
    //   cargo triplet fields, on a never-saved scratchpad counts as
    //   a user edit. Matches the canonicalize() path used for
    //   persisted-draft diffs.
    if (draft.insuranceRequested === true) return true;
    if (draft.insuranceCargoValue !== null && draft.insuranceCargoValue !== undefined) return true;
    if (draft.insuranceCargoCurrency !== null && draft.insuranceCargoCurrency !== undefined) {
      return true;
    }
    // WHY (#1448): STANDARD request-level totals on a never-saved scratchpad
    //   count as user edits — mirror the canonicalize() path so typing only
    //   the weight/volume block flips dirty and the close guard fires.
    if (draft.totalWeight !== null) return true;
    if (draft.totalVolume !== null) return true;
    return false;
  }
  return canonicalize(draft) !== canonicalize(baseline);
}

function canonicalize(d: DraftScratchpad): string {
  return JSON.stringify({
    name: d.name,
    route: d.route,
    // WHY (#700): destinations participate in the dirty diff. Order is
    //   meaningful (sortOrder is authoritative on the wire), so we
    //   serialise the list as-is rather than after a re-sort.
    destinations: d.destinations.map((dest) => ({
      arrivalCityId: dest.arrivalCityId,
      sortOrder: dest.sortOrder,
    })),
    // WHY (#1057): deliveryAddress participates in the dirty diff so a
    //   typed character on a CONTAINER scratchpad flips the editor
    //   into dirty state and the save/dirty-close guards engage.
    deliveryAddress: d.deliveryAddress,
    // WHY (#1393): mirror `deliveryAddress` — pickupAddress participates
    //   in the dirty diff so a typed character on a CONTAINER scratchpad
    //   flips the editor into dirty state.
    pickupAddress: d.pickupAddress,
    // WHY (#1061): the insurance triplet participates in the diff so
    //   an insurance-only edit on a persisted draft (e.g. flipping the
    //   toggle on/off or changing cargo value/currency) reaches dirty=
    //   true and Save / Send can persist the change. Normalised to
    //   `false` / `null` for missing keys so the canonical form is
    //   stable across legacy fixtures.
    insuranceRequested: d.insuranceRequested ?? false,
    insuranceCargoValue: d.insuranceCargoValue ?? null,
    insuranceCargoCurrency: d.insuranceCargoCurrency ?? null,
    // WHY (#996): Roads/Packaging IDs participate in the dirty diff.
    //   Order is preserved (caller insertion order); the wire treats
    //   the array as a set so dupes are rejected, but ordering does
    //   not change the persisted state — still serialise as-is to
    //   keep the canonical form deterministic for cache equality.
    roadOptionIds: [...(d.roadOptionIds ?? [])],
    packagingOptionIds: [...(d.packagingOptionIds ?? [])],
    // WHY (#1448): STANDARD request-level totals participate in the dirty
    //   diff so editing the weight/volume block on a persisted STANDARD
    //   draft flips dirty=true and Save / Send can persist the change.
    totalWeight: d.totalWeight,
    totalVolume: d.totalVolume,
    items: d.items.map((it) => ({
      id: it.id,
      name: it.name,
      brand: it.brand,
      material: it.material,
      weight: it.weight,
      volume: it.volume,
      quantity: it.quantity,
      productQuantity: it.productQuantity,
      description: it.description,
      photos: it.photos,
      // WHY (#590): dimensions-mode toggle and package rows count as
      //   dirty edits. Sorted by clientPackageId so reorder noise does
      //   not show up here; the editor controls sortOrder via array
      //   position, but the server treats the explicit `sortOrder`
      //   field as authoritative.
      dimensionsMode: it.dimensionsMode,
      packages: it.packages.map((p) => ({
        clientPackageId: p.clientPackageId,
        id: p.id,
        widthCm: p.widthCm,
        depthCm: p.depthCm,
        heightCm: p.heightCm,
        weightKg: p.weightKg,
        count: p.count,
      })),
    })),
    // WHY (#599): boxes participate in the dirty diff. The
    //   `boxesSectionOpen` flag is intentionally NOT included — it is
    //   pure UI state (does not round-trip the wire) and toggling it
    //   without editing any box should not mark the scratchpad dirty.
    boxes: d.boxes.map((b) => ({
      clientBoxId: b.clientBoxId,
      id: b.id,
      count: b.count,
      weightKg: b.weightKg,
      volumeM3: b.volumeM3,
      volumeMode: b.volumeMode,
      widthCm: b.widthCm,
      depthCm: b.depthCm,
      heightCm: b.heightCm,
      items: b.items.map((it) => ({
        clientBoxItemId: it.clientBoxItemId,
        id: it.id,
        name: it.name,
        brand: it.brand,
        material: it.material,
        description: it.description,
        quantity: it.quantity,
        photos: it.photos,
      })),
    })),
  });
}

/**
 * @aiPurpose Pure transformation that computes the post-save store
 *   patch from a save response. Lives outside the store so the
 *   id-remap logic can be tested in isolation and selectors that
 *   compose this with other reductions stay deterministic.
 *
 * INVARIANT: `sentSnapshot` is the scratchpad as it existed when the
 *   save was DISPATCHED (not the live state at apply time). Baseline
 *   is built from this snapshot — if the user kept typing while the
 *   save was in-flight, those later edits are NOT reflected in the
 *   persisted row and so MUST keep the live draft marked dirty
 *   against the baseline.
 */
export function computeApplySaveResult(
  live: DraftScratchpad,
  prevUnsavedPhotoKeys: readonly string[],
  input: {
    persistedId: string;
    itemIdMap: ReadonlyArray<{ clientItemId: string; id: string }>;
    packageIdMap?: ReadonlyArray<{ clientItemId: string; clientPackageId: string; id: string }>;
    boxIdMap?: ReadonlyArray<{ clientBoxId: string; id: string }>;
    boxItemIdMap?: ReadonlyArray<{ clientBoxId: string; clientBoxItemId: string; id: string }>;
    sentSnapshot: DraftScratchpad;
  },
): {
  draft: DraftScratchpad;
  draftPersistedId: string;
  baselineDraft: DraftScratchpad;
  unsavedPhotoKeys: string[];
} {
  const { persistedId, itemIdMap, packageIdMap, boxIdMap, boxItemIdMap, sentSnapshot } = input;
  const map = new Map<string, string>();
  for (const entry of itemIdMap) map.set(entry.clientItemId, entry.id);
  // WHY (#599): same `clientBoxId → server UUID` rebase pattern
  //   that items + packages use. Nested-item ids are keyed by
  //   `(clientBoxId, clientBoxItemId)` because the box was the
  //   ownership anchor on the wire. Stale boxes the user added
  //   AFTER the save dispatched keep their `draft-bx-N` ids
  //   until the next save claims them.
  const boxMap = new Map<string, string>();
  if (boxIdMap) {
    for (const entry of boxIdMap) boxMap.set(entry.clientBoxId, entry.id);
  }
  const boxItemByBox = new Map<string, Map<string, string>>();
  if (boxItemIdMap) {
    for (const entry of boxItemIdMap) {
      let inner = boxItemByBox.get(entry.clientBoxId);
      if (!inner) {
        inner = new Map<string, string>();
        boxItemByBox.set(entry.clientBoxId, inner);
      }
      inner.set(entry.clientBoxItemId, entry.id);
    }
  }
  // WHY (#590): `clientItemId` is the *pre-save* id the client sent
  //   (`draft-it-N` for new items, `it-NNNN` for already-persisted).
  //   The package id map keys back through that same value, so we
  //   build a nested lookup keyed by the pre-save item id.
  const pkgByClientItem = new Map<string, Map<string, string>>();
  if (packageIdMap) {
    for (const entry of packageIdMap) {
      let inner = pkgByClientItem.get(entry.clientItemId);
      if (!inner) {
        inner = new Map<string, string>();
        pkgByClientItem.set(entry.clientItemId, inner);
      }
      inner.set(entry.clientPackageId, entry.id);
    }
  }
  // Live draft: remap any items whose client ids matched the
  // server's response so subsequent saves diff against the
  // canonical row. Items the user added AFTER the save dispatched
  // keep their `draft-it-N` ids until a future save claims them.
  const remappedLiveItems = live.items.map((it) => {
    const next = map.get(it.id);
    const pkgMap = pkgByClientItem.get(it.id);
    const remappedPackages = pkgMap
      ? it.packages.map((p) => {
          const serverId = pkgMap.get(p.clientPackageId);
          return serverId ? { ...p, id: serverId } : p;
        })
      : it.packages;
    if (next) return { ...it, id: next, packages: remappedPackages };
    return pkgMap ? { ...it, packages: remappedPackages } : it;
  });
  const remappedLiveBoxes = live.boxes.map((b) => {
    const nextBoxId = boxMap.get(b.clientBoxId);
    const inner = boxItemByBox.get(b.clientBoxId);
    const remappedBoxItems = inner
      ? b.items.map((it) => {
          const serverId = inner.get(it.clientBoxItemId);
          return serverId ? { ...it, id: serverId } : it;
        })
      : b.items;
    if (nextBoxId) return { ...b, id: nextBoxId, items: remappedBoxItems };
    return inner ? { ...b, items: remappedBoxItems } : b;
  });
  const liveDraft: DraftScratchpad = {
    ...live,
    items: remappedLiveItems,
    boxes: remappedLiveBoxes,
  };
  // Baseline mirrors the SENT snapshot (what the server actually
  // committed), with the same id remapping applied so dirty
  // diffs against the canonical row. Edits the user typed during
  // the in-flight save remain dirty against this baseline.
  const baselineItems = sentSnapshot.items.map((it) => {
    const remapped = map.get(it.id);
    const pkgMap = pkgByClientItem.get(it.id);
    const baselinePackages = it.packages.map((p) => {
      const serverId = pkgMap?.get(p.clientPackageId) ?? p.id;
      return { ...p, id: serverId };
    });
    return {
      ...it,
      id: remapped ?? it.id,
      photos: [...it.photos],
      packages: baselinePackages,
    };
  });
  const baselineBoxes = sentSnapshot.boxes.map((b) => {
    const inner = boxItemByBox.get(b.clientBoxId);
    const remappedBoxItems = b.items.map((it) => {
      const serverId = inner?.get(it.clientBoxItemId) ?? it.id;
      return { ...it, id: serverId, photos: [...it.photos] };
    });
    return {
      ...b,
      id: boxMap.get(b.clientBoxId) ?? b.id,
      items: remappedBoxItems,
    };
  });
  const baseline: DraftScratchpad = {
    ...sentSnapshot,
    route: { from: sentSnapshot.route.from, to: sentSnapshot.route.to },
    destinations: sentSnapshot.destinations.map((dest) => ({
      arrivalCityId: dest.arrivalCityId,
      sortOrder: dest.sortOrder,
    })),
    // WHY (#996): clone the dictionary id arrays for the baseline so
    //   it does not alias the live slot (would defeat dirty-detection).
    roadOptionIds: [...(sentSnapshot.roadOptionIds ?? [])],
    packagingOptionIds: [...(sentSnapshot.packagingOptionIds ?? [])],
    items: baselineItems,
    boxes: baselineBoxes,
  };
  // Photos that landed in the saved baseline are no longer
  // "unsaved" — drop them from the cleanup batch.
  const baselineKeys = new Set(collectPhotoKeys(baseline));
  const remainingUnsaved = prevUnsavedPhotoKeys.filter((k) => !baselineKeys.has(k));
  return {
    draft: liveDraft,
    draftPersistedId: persistedId,
    baselineDraft: baseline,
    unsavedPhotoKeys: remainingUnsaved,
  };
}
